import React, { Fragment } from 'react';
import { dispatcher } from 'dispatcher';
import { Icon } from 'js/components/common/Icon';
import { compose, omit, path, pick } from 'ramda';
import { withTranslations } from 'js/components/hocs/withTranslations';
import { lifecycle, mapProps, withHandlers, withProps, withState } from 'recompose';
import { JackpotBare, withJackpot, withJackpotRef } from 'js/components/common/Jackpot';
import { useGameConfigurationSelector, useDeviceSelector, useJackpotsSelector } from 'js/store/hooks';

import { trackJackpotVisibility } from '../trackingApi';

import style from './index.scss';

// style.shine, style.sparkle
export const ANIMATION_DURATION = 2500;

let observer;
let visibleEntries = [];
let isAnimatedTimeout;

export const setRandomAnimatedItem = () => {
    clearTimeout(isAnimatedTimeout);

    if (visibleEntries.length) {
        if (visibleEntries.length > 1) {
            const isAnimatedItem = visibleEntries.find(entry => entry.isAnimated);

            visibleEntries = visibleEntries.filter(entry => !entry.isAnimated);
            visibleEntries[Math.floor(Math.random() * (visibleEntries.length - 1))].isAnimated = true;

            if (isAnimatedItem) {
                isAnimatedItem.isAnimated = false;
                visibleEntries = [...visibleEntries, isAnimatedItem];
            }
        } else {
            visibleEntries[0].isAnimated = true;
        }

        isAnimatedTimeout = window.setTimeout(setRandomAnimatedItem, ANIMATION_DURATION);
    }

    trackJackpotVisibility(visibleEntries);
};

export const onChangeIntersectionObserver = (entries) => {
    visibleEntries = entries.reduce((memo, { isIntersecting, target }) => isIntersecting
        ? [...memo, { target }]
        : memo.filter(item => item.target !== target), visibleEntries);

    setRandomAnimatedItem();
};

export const getRootMargin = (os, browserName, isMobile, isTablet) => {
    // skip dead zones
    const MOBILE_IOS_SAFARI = '0px 0px 160px 0px';
    const MOBILE_IOS_CHROME = '0px 0px 60px 0px';
    const TABLET_IOS_SAFARI = '0px 0px 30px 0px';
    const TABLET_IOS_CHROME = '0px 0px 60px 0px';

    const COMMON = '0px 0px 0px 0px';

    if (os === 'ios' && isMobile) {
        return browserName === 'chrome' ? MOBILE_IOS_CHROME : MOBILE_IOS_SAFARI;
    }

    if (os === 'ios' && isTablet) {
        return browserName === 'chrome' ? TABLET_IOS_CHROME : TABLET_IOS_SAFARI;
    }

    return COMMON;
};

export const JackpotLabel = ({ isAnimated, className = '', code, setJackpotRef, setNgpJackpotRef, t }) => {
    const { betDependent } = useJackpotsSelector(path(['jackpots', code])) || {};

    return (
        <div
            ref={setNgpJackpotRef}
            data-jackpot-code={code}
            className={`${style.jackpot} ${className}`}
        >
            {isAnimated && (
                <Fragment>
                    <span className={style.shine} />
                    <span className={style.sparkle} />
                </Fragment>
            )}
            <span className={style.title}>
                {t('playtech.mobile.system.jackpot')}
                {betDependent && <Icon type="gamesJackpotLimitsInfo" className={style.infoIcon} />}
            </span>
            <JackpotBare setJackpotRef={setJackpotRef} />
        </div>
    );
};

export default compose(
    withProps(({ gameCode }) => ({
        ...useDeviceSelector(pick(['os', 'isMobile', 'isTablet', 'browserName'])),
        code: useGameConfigurationSelector(path(['games', gameCode, 'jackpotCode']))
    })),
    mapProps(props => ({
        enableHitTime: props.showGuaranteedHitTime,
        ...omit(['gameCode', 'dispatch', 'showGuaranteedHitTime'])(props)
    })),
    withJackpot,
    withState('pause', 'setPause', true),
    withState('isAnimated', 'setIsAnimated', false),
    withHandlers(({ setPause, setIsAnimated }) => {
        let ngpJackpotRef;

        return {
            setNgpJackpotRef: ({ setNgpJackpotRef }) => (ref) => {
                ngpJackpotRef = ref;
                setNgpJackpotRef && setNgpJackpotRef(ref);
            },
            getJackpotRef: () => () => ngpJackpotRef,
            setVisibility: ({ pause, isAnimated }) => (entries) => {
                const currentEntry = entries.find(item => item.target === ngpJackpotRef);

                if (currentEntry) {
                    pause && setPause(false);
                    isAnimated !== currentEntry.isAnimated && setIsAnimated(currentEntry.isAnimated);
                } else {
                    !pause && setPause(true);
                    isAnimated && setIsAnimated(false);
                }
            }
        };
    }),
    withJackpotRef,
    withTranslations,
    lifecycle({
        componentDidMount() {
            const { getJackpotRef, setVisibility, os, isMobile, isTablet, browserName } = this.props;

            if (!observer) {
                observer = new IntersectionObserver(onChangeIntersectionObserver, {
                    rootMargin: getRootMargin(os, browserName, isMobile, isTablet),
                    threshold: [0]
                });
            }

            observer.observe(getJackpotRef());
            dispatcher.listen('jackpot:visibility', setVisibility);
        },
        componentWillUnmount() {
            observer.unobserve(this.props.getJackpotRef());
            dispatcher.removeListener('jackpot:visibility', this.props.setVisibility);
        }
    })
)(JackpotLabel);
